// Optimistic app-shell skeleton shown during the auth-claims load (IMP-046 P2).
//
// Instead of a full-screen BrandSplash while the user's custom claims resolve,
// we render the AppShell's structure (sidebar / header / content area) with
// pulsing placeholders, so the app's shape appears immediately and the swap to
// real content is a content-only change rather than a whole-screen flip.
//
// This is purely presentational — it makes NO Firestore reads and consults NO
// auth state, so it can never leak privileged content before claims resolve
// (client role checks are advisory; CLAUDE.md rule #6). The real auth decision
// happens in ProtectedRoute once `loading` clears. Layout mirrors AppShell so
// there is no jump when the real shell mounts. The real Logo is shown (static
// brand asset, no data) for reassurance; everything else is a `.skeleton` block.
//
// Heights are deliberately kept off the audited control-height scale, and the
// corner radius lives in the `.skeleton` CSS recipe, so the loading shell adds
// no off-standard pattern drift to the UI audit (scripts/audit-ui.mjs). NB: the
// audit greps raw source (comments included), so avoid writing audited class
// names literally even in prose here.

import { Logo } from './Logo';

export function AppShellSkeleton() {
  return (
    <div
      className="flex h-full min-h-dvh flex-col bg-n-50"
      aria-busy="true"
      aria-label="Loading"
    >
      <div className="flex flex-1 flex-col md:flex-row min-h-0">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex md:w-60 md:flex-col md:border-r md:border-cloud md:bg-white">
          <div className="flex h-16 items-center border-b border-cloud px-4">
            <Logo height={28} />
          </div>
          <nav className="flex flex-1 flex-col gap-3 px-4 py-5">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="skeleton h-5 w-full" />
            ))}
          </nav>
          <div className="border-t border-cloud px-4 py-3">
            <div className="skeleton h-6 w-full" />
          </div>
        </aside>

        {/* Mobile header */}
        {/* Same box as the real mobile header (Header.tsx): 56px below the
            status-bar inset, so the swap to the real shell does not jump. */}
        <header className="box-content flex h-14 items-center justify-between border-b border-cloud bg-white px-4 pt-safe md:hidden">
          <Logo height={24} />
          <div className="skeleton h-6 w-6" />
        </header>

        {/* Main content placeholder */}
        <main className="flex-1 overflow-y-auto px-4 pb-24 pt-4 md:px-8 md:pb-8 md:pt-6">
          <div className="mx-auto flex max-w-4xl flex-col gap-4">
            <div className="skeleton h-6 w-40" />
            <div className="skeleton h-4 w-64" />
            <div className="flex flex-col gap-3 pt-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="skeleton h-20 w-full" />
              ))}
            </div>
          </div>
        </main>

        {/* Mobile bottom nav */}
        <div className="fixed inset-x-0 bottom-0 z-30 flex h-16 items-center justify-around border-t border-cloud bg-white md:hidden">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton h-6 w-10" />
          ))}
        </div>
      </div>
    </div>
  );
}
